// Record a spouse on ONE contact — Donna's ask, 2026-08-26.
//
// The problem: several investors are on the symposium list as a husband with
// an email and a wife with none, on two separate records. The wife can never
// be sent anything, so the invitation the husband receives says "Dear Marc,"
// and from her side she is simply not invited. "The husbands like them to feel
// invited as well."
//
// The fix is NOT to merge them as duplicates — that tool deletes the other
// name, which is the exact opposite of what is wanted here. It is to name both
// people on the one invitation that does get delivered.
//
// TWO PERMISSION LEVELS, on purpose:
//   · Recording the spouse's name is an ordinary field edit (crm.use). It goes
//     through the normal store write-through, so it gets the same optimistic
//     update, Saved toast and failed-save banner as any other edit — and Donna
//     can do it without an admin.
//   · Folding the spouse's own record away REMOVES a record, so it is
//     crm.manage and opt-in. It is never the default: a spouse who has her own
//     email, account or history should keep her record and simply be named on
//     his invitation too.

import { useMemo, useState } from 'react'
import { Heart, AlertTriangle, Search } from 'lucide-react'

import { Modal, Button, Field, TextInput } from '@/components/ui'
import { useStore, useHasPermission } from '@/store/useStore'
import { useApiToken } from '@/auth/useApiToken'
import { api } from '@/api/client'
import { contactName, isInternalContact } from '@/lib/selectors'
import { refreshDataset } from '@/store/refreshDataset'
import type { Contact } from '@/types/entities'
import './MergeDuplicates.css'

/** A spouse with a life of her own in the CRM should keep her record. Absorb
 *  is offered only when there is demonstrably nothing to lose. */
function absorbIsSafe(c: Contact): { safe: boolean; because: string } {
  if (c.workEmail || c.personalEmail) {
    return { safe: false, because: 'they have their own email address, so they can be written to directly' }
  }
  if (c.accountId) {
    return { safe: false, because: 'they are linked to an account' }
  }
  if (c.title) {
    return { safe: false, because: 'they have a job title recorded' }
  }
  return { safe: true, because: '' }
}

export function SpouseModal({ open, onClose, contact }: {
  open: boolean; onClose: () => void; contact: Contact
}) {
  const data = useStore((s) => s.data)
  const patch = useStore((s) => s.patch)
  const canManage = useHasPermission('crm.manage')
  const getToken = useApiToken()

  const [mode, setMode] = useState<'type' | 'pick'>('type')
  const [typed, setTyped] = useState(contact.spouse ?? '')
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Contact | null>(null)
  const [absorb, setAbsorb] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q.length < 2) return []
    return data.contacts
      .filter((c) => c.id !== contact.id && !c.archived && !isInternalContact(c))
      .filter((c) => contactName(c).toLowerCase().includes(q))
      .slice(0, 8)
  }, [data.contacts, query, contact.id])

  const safety = picked ? absorbIsSafe(picked) : null
  const spouseName = mode === 'pick' && picked ? contactName(picked) : typed.trim()
  const canSave = spouseName.length > 0

  const save = async () => {
    setBusy(true); setError(null)
    try {
      if (mode === 'pick' && picked && absorb && canManage) {
        const token = await getToken()
        await api.setSpouse(token, contact.id, { spouseContactId: picked.id, absorb: true })
        await refreshDataset()
      } else {
        // Ordinary field edits — write-through, optimistic, crm.use.
        patch('contacts', contact.id, { married: true, spouse: spouseName })
        if (mode === 'pick' && picked) {
          // Both records say it. A one-directional link is the kind of
          // half-truth that later reads as a data error.
          patch('contacts', picked.id, { married: true, spouse: contactName(contact) })
        }
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      title={`Spouse — ${contactName(contact)}`}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Saving…' : absorb ? 'Save and combine' : 'Save'}
          </Button>
        </>
      }
    >
      {error && <div className="mdup-error" role="alert"><AlertTriangle size={14} /><span>{error}</span></div>}

      <p className="mdup-intro">
        <Heart size={13} />
        Their invitation will be addressed to both of them — “Dear {contact.firstName} and{' '}
        {(spouseName.split(/\s+/)[0] || '…')},” — and the invitation list will show them as a couple.
      </p>

      <div className="sp-tabs">
        <button type="button" className={`sp-tab${mode === 'type' ? ' sp-tab--on' : ''}`} onClick={() => setMode('type')}>
          Type a name
        </button>
        <button type="button" className={`sp-tab${mode === 'pick' ? ' sp-tab--on' : ''}`} onClick={() => setMode('pick')}>
          They have their own contact record
        </button>
      </div>

      {mode === 'type' ? (
        <Field label="Spouse’s name" required hint="First name is enough — “Pam”. A full name is fine too.">
          <TextInput value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="e.g. Pam" />
        </Field>
      ) : (
        <>
          <Field label="Find their record" required>
            <TextInput
              value={picked ? contactName(picked) : query}
              onChange={(e) => { setPicked(null); setAbsorb(false); setQuery(e.target.value) }}
              placeholder="Start typing a name…"
            />
          </Field>
          {!picked && matches.length > 0 && (
            <div className="sp-results">
              {matches.map((c) => (
                <button key={c.id} type="button" className="sp-result" onClick={() => { setPicked(c); setQuery('') }}>
                  <Search size={12} />
                  <span>{contactName(c)}</span>
                  <span className="sp-result-sub">{c.workEmail || c.personalEmail || 'no email'}</span>
                </button>
              ))}
            </div>
          )}
          {picked && canManage && (
            <div className={`sp-absorb${safety?.safe ? '' : ' sp-absorb--caution'}`}>
              <label className="cform-check">
                <input
                  type="checkbox"
                  checked={absorb}
                  disabled={!safety?.safe}
                  onChange={(e) => setAbsorb(e.target.checked)}
                />
                {' '}Also combine their record into this one
              </label>
              <p className="sp-absorb-note">
                {safety?.safe ? (
                  <>
                    {contactName(picked)} has no email, account or title of their own, so nothing is
                    lost by keeping one record. Their invitation history moves across, the party size
                    becomes two, and their record is removed — saved in full first, so it can be undone.
                  </>
                ) : (
                  <>
                    <b>Not offered here:</b> {contactName(picked)} should keep their own record because{' '}
                    {safety?.because}. Both records will simply record each other as spouse, and the
                    invitation will name them both.
                  </>
                )}
              </p>
            </div>
          )}
          {picked && !canManage && (
            <p className="sp-absorb-note">
              Both records will record each other as spouse. Combining two records into one needs
              admin rights.
            </p>
          )}
        </>
      )}
    </Modal>
  )
}
